import { useCallback, useMemo, useState } from 'react';

import { LightbulbIcon, ZapIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import SearchBar from '@/components/atoms/SearchBar/SearchBar';
import { StickyScrollFade } from '@/components/atoms/StickyScrollFade/StickyScrollFade';
import {
  ComplexFilterBar,
  SavedFilterView,
} from '@/components/ui/custom/ComplexFilter/ComplexFilterBar';
import {
  ComplexFilterCombobox,
  FilterDefinition,
  SavedFilterItem,
} from '@/components/ui/custom/ComplexFilter/ComplexFilterCombobox';
import { FilterItem } from '@/components/ui/custom/ComplexFilter/types';
import { useScrollFade } from '@/hooks/useScrollFade';
import { SavedView, getFilterValues } from '@/hooks/useTableFilters';

interface RulesToolbarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  filters: FilterItem[];
  setFilter: (key: string, values: string[], exclude?: boolean) => void;
  removeFilter: (...keys: string[]) => void;
  resetFilters: () => void;
  activeFilterCount: number;
  savedViews: SavedView[];
  activeView: SavedView | null;
  hasUnsavedChanges: boolean;
  onSaveView: (name: string) => string | null;
  onUpdateView: () => void;
  onDeleteView: () => void;
  onRenameView: (name: string) => string | null;
  onApplyView: (view: SavedView) => void;
  onClearActiveView: () => void;
}

export const RulesToolbar = ({
  searchTerm,
  onSearchChange,
  filters,
  setFilter,
  removeFilter,
  resetFilters,
  activeFilterCount,
  savedViews,
  activeView,
  hasUnsavedChanges,
  onSaveView,
  onUpdateView,
  onDeleteView,
  onRenameView,
  onApplyView,
  onClearActiveView,
}: RulesToolbarProps) => {
  const { t } = useTranslation();
  const [openPillId, setOpenPillId] = useState<string | null>(null);
  const { scrollRef, showFade } = useScrollFade<HTMLDivElement>('top');

  const filterDefinitions = useMemo((): FilterDefinition[] => {
    return [
      {
        id: 'status',
        label: t('rules.status'),
        icon: LightbulbIcon,
        type: 'radio',
        options: [
          { label: t('rules.active'), value: 'active' },
          { label: t('rules.inactive'), value: 'inactive' },
        ],
      },
      {
        id: 'triggeredBy',
        label: t('rules.filters.triggered_by'),
        icon: ZapIcon,
        type: 'radio',
        options: [
          { label: t('rules.filters.starting_conversation'), value: 'first_message' },
          { label: t('rules.filters.responding_to_message'), value: 'any_user_message' },
          { label: t('rules.filters.after_agent_reply'), value: 'post_message' },
        ],
      },
    ];
  }, [t]);

  const filterBadges = useMemo(() => {
    const badges = [];

    const statusValues = getFilterValues(filters, 'status');
    const triggeredByValues = getFilterValues(filters, 'triggeredBy');

    if (statusValues.length > 0) {
      const statusDef = filterDefinitions.find((f) => f.id === 'status')!;
      const statusLabel = statusValues[0] === 'active' ? t('rules.active') : t('rules.inactive');
      badges.push({
        id: 'status',
        icon: LightbulbIcon,
        title: t('rules.status'),
        text: statusLabel,
        onRemove: () => removeFilter('status'),
        filterDefinition: {
          overlayLabel: statusDef.overlayLabel,
          type: statusDef.type,
          options: statusDef.options,
        },
        value: statusValues[0],
        onValueChange: (value: string | string[] | undefined) =>
          value ? setFilter('status', [value as string]) : removeFilter('status'),
      });
    }

    if (triggeredByValues.length > 0) {
      const triggeredByDef = filterDefinitions.find((f) => f.id === 'triggeredBy')!;
      const triggerLabel =
        triggeredByDef.options.find((o) => o.value === triggeredByValues[0])?.label ??
        triggeredByValues[0];
      badges.push({
        id: 'triggeredBy',
        icon: ZapIcon,
        title: t('rules.filters.triggered_by'),
        text: triggerLabel,
        onRemove: () => removeFilter('triggeredBy'),
        filterDefinition: {
          overlayLabel: triggeredByDef.overlayLabel,
          type: triggeredByDef.type,
          options: triggeredByDef.options,
        },
        value: triggeredByValues[0],
        onValueChange: (value: string | string[] | undefined) =>
          value ? setFilter('triggeredBy', [value as string]) : removeFilter('triggeredBy'),
      });
    }

    return badges;
  }, [filters, filterDefinitions, removeFilter, setFilter, t]);

  // Map saved views to dropdown format
  const savedFilterItems: SavedFilterItem[] = useMemo(
    () => savedViews.map((v) => ({ id: v.id, name: v.name })),
    [savedViews]
  );

  // Map active view to status bar format
  const activeSavedFilterView: SavedFilterView | null = useMemo(
    () => (activeView ? { id: activeView.id, name: activeView.name } : null),
    [activeView]
  );

  const handleApplySavedFilter = useCallback(
    (item: SavedFilterItem) => {
      const view = savedViews.find((v) => v.id === item.id);
      if (view) {
        onApplyView(view);
      }
    },
    [savedViews, onApplyView]
  );

  const handleClearFilters = useCallback(() => {
    resetFilters();
    onClearActiveView();
  }, [resetFilters, onClearActiveView]);

  return (
    <div
      ref={scrollRef}
      className="sticky top-0 z-30 flex flex-col gap-4 bg-bg-muted-light pt-4 pb-3 pl-6"
    >
      <StickyScrollFade show={showFade} />
      <div className="flex items-center justify-between pl-1" role="search">
        <SearchBar
          placeholder={t('rules.search')}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
        />

        <div className="flex items-center gap-2">
          <ComplexFilterCombobox
            filterDefinitions={filterDefinitions}
            filters={filters}
            setFilter={setFilter}
            removeFilter={removeFilter}
            activeFilterCount={activeFilterCount}
            savedFilters={savedFilterItems}
            onApplySavedFilter={handleApplySavedFilter}
            onOpenActivePill={setOpenPillId}
          />
        </div>
      </div>

      <ComplexFilterBar
        filters={filterBadges}
        savedFiltersConfig={{
          savedFilters: savedFilterItems,
          activeSavedFilter: activeSavedFilterView,
          hasUnsavedChanges,
          onSave: onSaveView,
          onUpdate: onUpdateView,
          onDelete: onDeleteView,
          onRename: onRenameView,
          onSelectView: (viewId) => {
            const view = savedViews.find((v) => v.id === viewId);
            if (view) {
              onApplyView(view);
            }
          },
        }}
        openPillId={openPillId}
        onOpenPillChange={setOpenPillId}
        onClearAll={handleClearFilters}
      />
    </div>
  );
};
